import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { Doc, ListMethodArg, ListMethodResponse, GetMethodResponse, ModelClass, References } from '/imports/types';
import type { BotMode } from '/imports/api/bots';
// Resolution lives in a Meteor-free module so it is unit testable; re-exported
// here so callers have one import for everything fleet-related.
export { resolveFleet, type FleetLike } from '/imports/api/fleetResolve';

/**
 * A fleet is a group of bots that share a voice, defined by a username PREFIX.
 *
 * Membership is derived, never stored on the bot: a bot belongs to the fleet
 * whose prefix its username starts with. That makes the grouping trivially
 * reversible -- delete the fleet and every bot in it falls straight back to the
 * catalog defaults, with no per-bot cleanup and no orphaned foreign keys.
 *
 * Prefix (not regex, not a list of names) because it matches how the fleet is
 * actually named: `MogBot` covers MogBot1, MogBots3, MogBotsA and
 * MogBotsSelling in one rule, and a bot added tomorrow joins automatically.
 */
export interface Fleet extends Doc {
  _id: string;
  /** Display name, e.g. 'MogBots'. */
  name: string;
  /** Username prefix that defines membership, e.g. 'MogBot'. Case-insensitive. */
  prefix: string;
  /** Bots carved OUT of the prefix's capture (case-insensitive usernames).
   *  An excluded bot falls through to the next-longest matching fleet, or to
   *  no fleet at all. Managed from the membership card on the fleet page. */
  excludedBots?: string[];
  /**
   * Mongo query selecting the cards this fleet advertises, run against `cards`.
   *
   * Unset falls back to Settings.classifiedQuery. A bot with an explicit
   * `config.classified` template ignores both -- that template picks its own
   * cards.
   *
   * Stored as a raw query object and passed straight to the driver. That is
   * deliberate (superuser-only surface) but it means a malformed or
   * over-narrow query silently empties the ads, which is why the form shows a
   * live match count.
   */
  classifiedQuery?: Record<string, unknown> | null;
  /**
   * Classified ad TEMPLATE for every bot in this fleet, with `{}` slots
   * filled from the classified card selection -- same syntax as the per-bot
   * config.classified, which overrides this when set. Controls ad FORMAT
   * only; which cards fill it still comes from the classified list chain.
   * Blank falls through to the mode-generated ad.
   */
  classified?: string | null;
  /** Card list refs. These replace the inline queries above -- the inline
   *  fields are LEGACY (kept for rollback, no longer read). A ref names a
   *  strategy in `cardLists`; editing the list updates every referencing fleet. */
  classifiedCardListId?: string | null;
  buyCardListId?: string | null;
  sellCardListId?: string | null;
  /**
   * Mongo query against `cards` selecting what this fleet's buy/hybrid bots
   * BUY. Unset inherits Settings.buyQuery; with neither set, the legacy
   * buyTargets collection still governs, so an unconfigured system trades
   * exactly as before the refactor.
   */
  buyQuery?: Record<string, unknown> | null;
  /**
   * Mongo query selecting what sell/hybrid bots STOCK AND SELL (the old
   * sellTargets allowlist). Price floors that used to live in
   * sellTargets.minprice belong in here, e.g. {"manSellPrice": {"$gt": 2}}.
   */
  sellQuery?: Record<string, unknown> | null;
  /** Max copies of any bought card to OWN (the old per-target buycap, which was
   *  4 on every one of the 100k rows). Unset inherits Settings.buyCap, then 4. */
  buyCap?: number | null;
  /** Stock level to hold per sold card (the old sellTargets.sellcap, never set
   *  in practice). Unset inherits Settings.sellCap, then bot.options.stocksize. */
  sellCap?: number | null;
  /** Replenish priority for this fleet's bots (higher = first at the depot).
   *  A bot's own config.replenishPriority overrides this. */
  replenishPriority?: number | null;
  /** Floor buy price for unpriced cards, fleet level. A bot's own
   *  config.defaultBuyPrice overrides this; blank inherits Settings. */
  defaultBuyPrice?: number | null;
  /** Floor sell price for junk (cards with neither real price), fleet level.
   *  A bot's own config.defaultSellPrice overrides; blank inherits Settings. */
  defaultSellPrice?: number | null;
  /** Buy on credit past the ticket balance, fleet level. A bot's own
   *  config.overdraft overrides this (including to false); unset inherits
   *  Settings.overdraft. */
  overdraft?: boolean | null;
  /** Inventory policy ref: per-card stock QUANTITIES for this fleet's
   *  sell/hybrid bots. Membership still comes from the sell selection; the
   *  policy only refines how many copies to hold. A bot's own
   *  config.inventoryPolicyId overrides this. Unset = sellCap/stocksize. */
  inventoryPolicyId?: string | null;
}

type Model = Fleet;
type Refs = References<Model>;

export const Fleets = new Mongo.Collection<Fleet>('fleets');

export const listFleets = DeclareMethod<ListMethodArg<Fleet>, ListMethodResponse<Fleet, Refs>>('listFleets');
export const getFleet = DeclareMethod<string, GetMethodResponse<Fleet, Refs>>('getFleet');
export const createFleet = DeclareMethod<Partial<Fleet>, string>('createFleet');
export const updateFleet = DeclareMethod<Partial<Fleet>, string>('updateFleet');
export const deleteFleet = DeclareMethod<string, undefined>('deleteFleet');

/**
 * Set every member bot's mode in one call.
 *
 * `matched` is the whole membership and `updated` only those that actually
 * changed, so "12 bots, 0 changed" reads as already-correct rather than as a
 * failure. `running` names the members that were up at the time: a bot reads
 * its mode once when it starts, so those keep trading in their old mode until
 * they are restarted, and the caller has to be able to say so.
 */
export const setFleetMode = DeclareMethod<{ fleetId: string; mode: BotMode }, {
  matched: number;
  updated: number;
  running: string[];
}>('setFleetMode');

/** Which bots a prefix would capture — used by the Fleet form to preview the
 *  effect of a prefix before it is saved. */
export const previewFleetMembers = DeclareMethod<{ prefix: string; fleetId?: string }, {
  members: { username: string; mode: string; stolenFrom?: string }[];
  /** Bots the prefix would capture but that sit on the fleet's exclusion
   *  list -- the "excluded from this fleet" card. */
  excluded: { username: string; mode: string }[];
  total: number;
}>('previewFleetMembers');

/** How many cards a candidate classified query matches, with a sample. */
export const previewClassifiedQuery = DeclareMethod<{ query: unknown }, {
  ok: boolean;
  error?: string;
  count: number;
  sample: { name: string; set: string; manBuyPrice: number | null; manSellPrice: number | null }[];
}>('previewClassifiedQuery');

export const modelClass: ModelClass<Model, Refs> = {
  collection: Fleets,
  editableFields: ['name', 'prefix', 'excludedBots', 'classified', 'classifiedCardListId', 'buyCardListId', 'sellCardListId', 'buyCap', 'sellCap', 'replenishPriority', 'defaultBuyPrice', 'defaultSellPrice', 'overdraft', 'inventoryPolicyId'],
  searchQuery: (search?: string) => {
    search = search?.trim();
    return search?.length
      ? { $or: [{ name: new RegExp(search, 'i') }, { prefix: new RegExp(search, 'i') }] } as any
      : {};
  },
  listMethod: listFleets,
  getMethod: getFleet,
  createMethod: createFleet,
  updateMethod: updateFleet,
  deleteMethod: deleteFleet,
  pathPrefix: '/fleets',
  modelName: 'Fleet',
  pluralName: 'Fleets',
  icon: 'boxes',
};
